import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';

import { Logout } from './components/auth/Logout';
import { RedirectHandler } from './components/auth/RedirectHandler';
import { Loader } from './ui/components/Loader';
import { RouteError } from './components/MainLayout/RouteError';
import { docRoutes } from './routesDocs';

const Root = lazy(() => import('@/Root'));

import PlainViewErrorWrapper from '@/components/views/PlainViewErrorWrapper';

const ViewErrorWrapper = lazy(() => import('@/components/views/ViewErrorWrapper'));
const EmptyView = lazy(() => import('@/components/views/EmptyView'));

const MainLayout = lazy(() => import('@/components/MainLayout/MainLayout'));
const AuthWrapper = lazy(() => import('@/components/MainLayout/AuthWrapper'));
const ProjectParent = lazy(() => import('@/components/views/ProjectParent'));
const ProjectView = lazy(() => import('@/components/views/ProjectView'));

const UserTokenView = lazy(() => import('@/components/views/UserTokenView'));

const ZoneParent = lazy(() => import('@/components/views/Airflow/ZoneParent'));
const ZoneDesign = lazy(() => import('@/components/views/Airflow/ZoneDesign'));
const ZoneView = lazy(() => import('@/components/views/Airflow/ZoneView'));
const ZoneSummaryView = lazy(() => import('@/components/views/Airflow/ZoneSummaryView'));

const PlannerParent = lazy(() => import('@/components/views/PlannerParent'));
const PlannerView = lazy(() => import('@/components/views/PlannerView'));
const Planner3dView = lazy(() => import('@/components/views/Planner3dView'));
const RoomSummaryView = lazy(() => import('@/components/views/RoomSummaryView'));

const ReportView = lazy(() => import('@/components/views/Report/ReportView'));

export const createRouter = () =>
  createBrowserRouter([
    { path: '/auth', Component: RedirectHandler },
    {
      element: (
        <PlainViewErrorWrapper>
          <Suspense fallback={<Loader fullscreen />}>
            <Root />
          </Suspense>
        </PlainViewErrorWrapper>
      ),
      children: [
        { path: '/logout', Component: Logout },
        {
          path: '/',
          Component: AuthWrapper,
          errorElement: <RouteError />,
          children: [
            {
              path: '/',
              Component: MainLayout,
              children: [
                { index: true, Component: EmptyView },
                {
                  path: '/project/:projectId?',
                  // This component makes sure the children are not shown before we have data available
                  element: (
                    <ViewErrorWrapper view="project">
                      <ProjectParent />
                    </ViewErrorWrapper>
                  ),
                  children: [
                    { index: true, Component: ProjectView },
                    {
                      path: 'room/:roomId',
                      element: (
                        <ViewErrorWrapper view="planner">
                          <Suspense fallback={<Loader fullscreen />}>
                            <PlannerParent />
                          </Suspense>
                        </ViewErrorWrapper>
                      ),
                      children: [
                        {
                          index: true,
                          element: (
                            <Suspense fallback={<Loader fullscreen />}>
                              <PlannerView />
                            </Suspense>
                          ),
                        },
                        {
                          path: '3d',
                          element: (
                            <Suspense fallback={<Loader fullscreen />}>
                              <Planner3dView />
                            </Suspense>
                          ),
                        },
                        {
                          path: 'summary',
                          element: (
                            <Suspense fallback={<Loader fullscreen />}>
                              <RoomSummaryView />
                            </Suspense>
                          ),
                        },
                      ],
                    },
                    {
                      path: 'zone/:zoneId',
                      element: (
                        <ViewErrorWrapper view="zone">
                          <Suspense fallback={<Loader fullscreen />}>
                            <ZoneParent />
                          </Suspense>
                        </ViewErrorWrapper>
                      ),
                      children: [
                        {
                          path: 'design',
                          element: (
                            <Suspense fallback={<Loader fullscreen />}>
                              <ZoneDesign />
                            </Suspense>
                          ),
                        },
                        {
                          path: 'view',
                          element: (
                            <Suspense fallback={<Loader fullscreen />}>
                              <ZoneView />
                            </Suspense>
                          ),
                        },
                        {
                          path: 'summary',
                          element: (
                            <Suspense fallback={<Loader fullscreen />}>
                              <ZoneSummaryView />
                            </Suspense>
                          ),
                        },
                      ],
                    },
                    {
                      path: 'report',
                      element: (
                        <Suspense fallback={<Loader fullscreen />}>
                          <ReportView />
                        </Suspense>
                      ),
                    },
                  ],
                },
                {
                  path: '/user/token',
                  Component: UserTokenView,
                },
                docRoutes,
                {
                  path: '*',
                  element: <Navigate to="/" />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]);
